/**
 * Pure align/distribute geometry for freeform blocks (UX Pack F4b).
 * Zero imports; node-testable. Consumes measurement items reported by the
 * iframe's query-rects-for-eids handler and produces per-eid override change
 * sets for applyElementOverridesPerEid — or a TYPED rejection. Distribute
 * REFUSES layouts it cannot model exactly (flex/grid parents, CSS gap,
 * transforms, positioned/hidden members, overlap, vertical writing modes,
 * non-adjacent siblings) rather than producing approximate results.
 *
 * Item shape: { eid, rect: {top,left,width,height}, tagName, replaced,
 *   computed: {display, position, transform, visibility} }
 * Parent shape: { display, gap, writingMode }
 *
 * Alignment vocabulary follows the image panel's margin semantics with two
 * load-bearing deviations: EXPLICIT '0' instead of '' (clearing falls back to
 * the block's INLINE style — the container block ships `margin:0 auto`, so ''
 * would leave "align left" centered; the assembler emits overrides with
 * !important, so explicit values win deterministically), and
 * `width:'fit-content'` for inline-level non-replaced roots (display:block
 * alone would stretch an anchor to full container width).
 */

const REPLACED_TAGS = new Set(['img', 'svg', 'picture', 'video', 'iframe', 'canvas']);

export function classifyBlockForAlign({ tagName, display, replaced } = {}) {
  const tag = String(tagName || '').toLowerCase();
  if (replaced || REPLACED_TAGS.has(tag)) return 'replaced';
  const disp = String(display || '');
  if (disp.startsWith('inline')) return 'inline';
  return 'block';
}

const ALIGN_SIDES = new Set(['left', 'center', 'right']);

function alignChangesFor(kind, side) {
  const margins =
    side === 'left'
      ? { marginLeft: '0', marginRight: 'auto' }
      : side === 'right'
        ? { marginLeft: 'auto', marginRight: '0' }
        : { marginLeft: 'auto', marginRight: 'auto' };
  if (kind === 'replaced') {
    return side === 'left' ? { display: '', ...margins } : { display: 'block', ...margins };
  }
  if (kind === 'inline') {
    return { display: 'block', width: 'fit-content', ...margins };
  }
  return margins;
}

/**
 * Horizontal box alignment for N≥2 blocks.
 * @returns {{changesByEid: object}|{error: string}}
 */
export function computeAlignChanges(items, side) {
  if (!ALIGN_SIDES.has(side)) return { error: 'bad-side' };
  const list = (Array.isArray(items) ? items : []).filter((item) => item?.eid);
  if (list.length < 2) return { error: 'need-two' };
  const changesByEid = {};
  for (const item of list) {
    const kind = classifyBlockForAlign({
      tagName: item.tagName,
      display: item.computed?.display,
      replaced: item.replaced
    });
    changesByEid[item.eid] = alignChangesFor(kind, side);
  }
  return { changesByEid };
}

function distributeGuardError(items, parent) {
  const parentDisplay = String(parent?.display || '');
  if (parentDisplay.includes('flex') || parentDisplay.includes('grid')) {
    return 'unsupported-parent';
  }
  const gap = String(parent?.gap || '').trim();
  if (gap && gap !== 'normal' && gap !== '0px' && gap !== 'normal normal') {
    return 'unsupported-parent';
  }
  const writingMode = String(parent?.writingMode || 'horizontal-tb');
  if (writingMode !== 'horizontal-tb') return 'vertical-writing';
  for (const item of items) {
    const computed = item.computed || {};
    const position = String(computed.position || 'static');
    if (position !== 'static' && position !== 'relative') return 'unsupported-member';
    if (computed.transform && computed.transform !== 'none') return 'unsupported-member';
    if (computed.visibility === 'hidden' || computed.display === 'none')
      return 'unsupported-member';
    if (!(item.rect?.height > 0) || !(item.rect?.width > 0)) return 'unsupported-member';
  }
  return null;
}

/**
 * Equalize vertical gaps between N≥3 ADJACENT sibling blocks. Writes both
 * sides of every pair (marginBottom:'0px' on 1..N−1, marginTop:'<G>px' on
 * 2..N) — exact under margin collapsing (max(0,G)=G) AND under
 * non-collapsing layouts (0+G=G), so no per-case solver is needed. The last
 * block's marginBottom is untouched (its gap to following content is not
 * part of the selection).
 *
 * @param {Array} items — measured members (any order; sorted by rect.top)
 * @param {{display,gap,writingMode}} parent — the shared parent's computed
 * @param {{contiguous?: boolean, sameParent?: boolean}} flags — iframe-verified
 * @returns {{changesByEid: object, gapPx: number}|{error: string}}
 */
export function computeDistributeChanges(items, parent, flags = {}) {
  const list = (Array.isArray(items) ? items : []).filter((item) => item?.eid && item.rect);
  if (list.length < 3) return { error: 'need-three' };
  if (flags.sameParent === false) return { error: 'different-parents' };
  if (flags.contiguous === false) return { error: 'not-contiguous' };
  const guard = distributeGuardError(list, parent);
  if (guard) return { error: guard };

  const sorted = [...list].sort((a, b) => a.rect.top - b.rect.top);
  const gaps = [];
  for (let i = 1; i < sorted.length; i += 1) {
    const previous = sorted[i - 1];
    const gapPx = sorted[i].rect.top - (previous.rect.top + previous.rect.height);
    if (gapPx < -0.5) return { error: 'overlap' };
    gaps.push(Math.max(0, gapPx));
  }
  const target = Math.max(0, Math.round(gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length));

  const changesByEid = {};
  for (let i = 0; i < sorted.length; i += 1) {
    const changes = {};
    if (i < sorted.length - 1) changes.marginBottom = '0px';
    if (i > 0) changes.marginTop = `${target}px`;
    changesByEid[sorted[i].eid] = changes;
  }
  return { changesByEid, gapPx: target };
}

/** User-facing copy for every typed rejection — one place, no ad-hoc strings. */
export const DISTRIBUTE_ERROR_COPY = Object.freeze({
  'need-two': 'Select at least two blocks to align',
  'need-three': 'Select at least three blocks to distribute',
  'not-contiguous': 'Select adjacent blocks to distribute — no gaps in between',
  'different-parents': 'Blocks must share the same container',
  'unsupported-parent': 'This container manages its own spacing (flex/grid gap)',
  'unsupported-member': 'A selected block is hidden, transformed, or positioned',
  overlap: 'Blocks overlap — distribute needs stacked blocks',
  'vertical-writing': 'Distribute supports horizontal writing modes only',
  'bad-side': 'Unknown alignment'
});
